import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { useLang, LanguageSwitcher } from "@/lib/lang";

export default function ClientVerify() {
  const { t } = useLang();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { refresh } = useAuth();
  const email = params.get("email") || "";
  const [digits, setDigits] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const inputs = useRef([]);

  useEffect(() => { inputs.current[0]?.focus(); }, []);
  useEffect(() => {
    if (cooldown === 0) return;
    const tm = setTimeout(() => setCooldown(cooldown - 1), 1000);
    return () => clearTimeout(tm);
  }, [cooldown]);

  const setDigit = (i, val) => {
    const clean = val.replace(/\D/g, "").slice(-1);
    const next = [...digits];
    next[i] = clean;
    setDigits(next);
    if (clean && i < 5) inputs.current[i + 1]?.focus();
  };
  const onKeyDown = (i, e) => {
    if (e.key === "Backspace" && !digits[i] && i > 0) inputs.current[i - 1]?.focus();
  };
  const onPaste = (e) => {
    e.preventDefault();
    const p = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, 6);
    if (!p) return;
    const arr = ["", "", "", "", "", ""];
    for (let i = 0; i < p.length; i++) arr[i] = p[i];
    setDigits(arr);
    inputs.current[Math.min(p.length, 5)]?.focus();
  };

  const submit = async (e) => {
    e.preventDefault();
    const code = digits.join("");
    if (code.length !== 6) { toast.error(t("toast_error_enter_6_digits")); return; }
    setLoading(true);
    try {
      await api.post("/client/verify", { email, code });
      await refresh();
      toast.success(t("client_account_verified"));
      nav("/client-portal");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const resend = async () => {
    if (cooldown > 0) return;
    try {
      await api.post("/client/resend-otp", { email });
      toast.success(t("toast_new_code_sent"));
      setCooldown(60);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="min-h-screen bg-[#0a0f1e] flex items-center justify-center p-6">
      <div className="w-full max-w-md" data-testid="client-verify-page">
        <div className="flex justify-end mb-4">
          <LanguageSwitcher testId="verify-lang-switch" className="text-slate-300" />
        </div>
        <div className="text-center mb-8">
          <div className="inline-flex w-14 h-14 border border-[#f59e0b] items-center justify-center mb-4">
            <span className="font-display text-[#f59e0b] text-3xl">D</span>
          </div>
          <h1 className="font-display text-3xl text-white">{t("client_verify_title")}</h1>
          <p className="text-slate-400 text-sm mt-2">{t("client_verify_subtitle_prefix")} <span className="text-[#f59e0b]">{email}</span></p>
        </div>

        <form onSubmit={submit} className="bg-white p-8 border border-slate-200 space-y-6">
          <div className="flex gap-2 justify-center" onPaste={onPaste}>
            {digits.map((d, i) => (
              <input
                key={i}
                ref={(el) => (inputs.current[i] = el)}
                value={d}
                onChange={(e) => setDigit(i, e.target.value)}
                onKeyDown={(e) => onKeyDown(i, e)}
                inputMode="numeric"
                maxLength={1}
                data-testid={`otp-digit-${i}`}
                className="w-12 h-14 border border-slate-300 text-center font-display text-2xl focus:border-[#f59e0b] focus:outline-none"
              />
            ))}
          </div>

          <button type="submit" disabled={loading || digits.join("").length !== 6} className="w-full h-11 bg-[#f59e0b] text-[#0a0f1e] font-bold text-sm uppercase tracking-wide hover:bg-[#f59e0b]/90 disabled:opacity-40" data-testid="otp-submit">
            {loading ? t("client_verifying") : t("client_verify_action")}
          </button>

          <div className="text-center text-xs text-slate-500">
            {t("client_didnt_receive")}{" "}
            <button type="button" onClick={resend} disabled={cooldown > 0} className="text-[#f59e0b] font-bold uppercase tracking-widest disabled:opacity-40" data-testid="otp-resend">
              {cooldown > 0 ? `${t("client_resend_in")} ${cooldown}s` : t("client_resend_code")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
